/**
 * DocumentEditor — R45 slice 2 Wave 2.
 *
 * Full-page rich-text editor for a single document. Auto-saves 2s after
 * the body/title changes. Uses the shared TipTap `RichTextEditor` so the
 * editing surface is identical to the diagram-node inspector.
 *
 * Layout:
 *   • Top toolbar: back, editable title, scope select, save state, delete
 *   • Body: TipTap editor (full toolbar)
 *   • Right side (collapsible): metadata (created / updated / owner / word count)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/diagrams/RichTextEditor";
import { ArrowLeft, Save, Trash2, Lock, Globe2, Users as UsersIcon, Package as PackageIcon, Building2, Maximize2, Minimize2 } from "lucide-react";

const SCOPE_ICONS = {
  private: Lock, workspace: Globe2, client: UsersIcon, brand: PackageIcon, project: Building2,
};

function stripHtml(html) {
  const el = document.createElement("div");
  el.innerHTML = html || "";
  return el.textContent || el.innerText || "";
}

export default function DocumentEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/documents/${id}`);
        if (cancel) return;
        setDoc(data);
        setTitle(data.title || "");
        setBody(data.body_html || "");
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load document")); }
    })();
    return () => { cancel = true; };
  }, [id]);

  const save = useCallback(async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/documents/${id}`, {
        title: title.trim() || "Untitled",
        body_html: body,
        body_text: stripHtml(body),
      });
      setDoc(data);
      setDirty(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  }, [id, title, body, doc]);

  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(save, 2000);
    return () => clearTimeout(t);
  }, [dirty, save]);

  const updateMeta = async (patch) => {
    try {
      const { data } = await api.patch(`/documents/${id}`, patch);
      setDoc(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const del = async () => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Deleted");
      nav("/app/documents");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const wordCount = useMemo(() => stripHtml(body).split(/\s+/).filter(Boolean).length, [body]);

  if (!doc) {
    return <div className="p-6 text-sm text-muted-foreground">Loading document…</div>;
  }
  const ScopeIcon = SCOPE_ICONS[doc.scope] || Lock;
  const shellCls = fullscreen ? "fixed inset-0 z-[80] bg-background flex flex-col" : "flex flex-col";

  return (
    <div className={shellCls} data-testid="document-editor">
      <div className="border-b bg-background flex items-center gap-2 px-3 py-2 flex-wrap">
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => fullscreen ? setFullscreen(false) : nav("/app/documents")} data-testid="document-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> {fullscreen ? "Exit fullscreen" : "Back"}
        </Button>
        <div className="border-l h-6 mx-1" />
        <Input
          value={title}
          onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
          onBlur={() => title !== doc.title && save()}
          className="h-8 rounded-none w-96 font-semibold"
          data-testid="document-title"
        />
        <div className="flex items-center gap-1 border h-8 px-2">
          <ScopeIcon className="w-3.5 h-3.5" />
          <Select value={doc.scope} onValueChange={(v) => { setDoc(d => ({ ...d, scope: v })); updateMeta({ scope: v }); }}>
            <SelectTrigger className="h-7 border-0 shadow-none rounded-none px-1 text-xs w-28" data-testid="document-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="private">Private</SelectItem>
              <SelectItem value="workspace">Workspace</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="brand">Brand</SelectItem>
              <SelectItem value="project">Project</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" className="rounded-none h-8" onClick={() => setFullscreen((f) => !f)} data-testid="document-fullscreen">
          {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </Button>
        <div className="flex-1" />
        <div className="text-xs text-muted-foreground">
          {saving ? "Saving…" : (dirty ? "Unsaved changes" : `v${doc.version} · ${wordCount} words · saved`)}
        </div>
        <Button size="sm" className="rounded-none h-8" onClick={save} disabled={saving} data-testid="document-save">
          <Save className="w-4 h-4 mr-1" /> Save
        </Button>
        <Button size="sm" variant="ghost" className="rounded-none h-8 text-rose-600" onClick={del} data-testid="document-delete">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto w-full">
        <RichTextEditor
          value={body}
          onChange={(html) => { setBody(html); setDirty(true); }}
          editable
          placeholder="Type '/' for commands, or just start writing… Use the toolbar for headings, lists, images and tables."
        />
      </div>
    </div>
  );
}
